import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { crewSchema } from "../../api/crewSchemas";
import type { Crew } from "../../api/crewSchemas";
import { useApiClient } from "../../api/ApiContext";
import { NetworkError } from "../../api/errors";
import type { LocalStore } from "../../offline/LocalStore";
import { useOffline } from "../../offline/OfflineContext";
import { useAuth } from "../../shared/auth/AuthContext";
import { useOwnerKey } from "../../shared/session/tenantSelection";

/** Refresh interval of the crew, like the team overview (ADR-0029). */
const crewRefreshMs = 60_000;

/** The crew with the time it was requested. */
export interface CrewResult {
  readonly crew: Crew;
  /** Device time when the request started, in milliseconds. */
  readonly fetchedAtMs: number;
  /** Whether the server could not be reached and the last known crew on the device is shown. */
  readonly fromDevice: boolean;
}

/**
 * Returns the last known crew of a person, or `null` if none is kept or it no longer fits the schema;
 * a kept crew is a convenience, so a storage that fails reads as none.
 *
 * @param store - The local store.
 * @param owner - Owner key of the session.
 * @returns The kept crew or `null`.
 */
async function readKept(store: LocalStore, owner: string): Promise<CrewResult | null> {
  const cached = await store.getCrewCache(owner).catch(() => null);
  const parsed = cached === null ? null : crewSchema.safeParse(cached.crew);
  const kept: CrewResult | null =
    cached === null || !parsed?.success
      ? null
      : { crew: parsed.data, fetchedAtMs: cached.fetchedAtMs, fromDevice: true };

  return kept;
}

/**
 * Loads the persons the signed-in manager or HR administrator may clock for (`GET /me/crew`, T-170)
 * and keeps them on the device, so that the crew on the clock screen works offline with the last known
 * list and states. The query key starts with `me`, so every completed synchronization reloads it.
 *
 * @param enabled - Whether the crew is open and the person holds `ClockForOthers`.
 * @returns The query result.
 */
export function useCrew(enabled: boolean): UseQueryResult<CrewResult> {
  const client = useApiClient();
  const offline = useOffline();
  const owner = useOwnerKey();
  const { user } = useAuth();
  const subject = user?.subject ?? null;
  const query = useQuery({
    queryKey: ["me", "crew", owner ?? ""] as const,
    queryFn: async ({ signal }): Promise<CrewResult> => {
      const fetchedAtMs = offline.nowMs();

      try {
        const crew = await client.get("/api/v1/me/crew", crewSchema, { signal });

        if (owner !== null && subject !== null) {
          // Keeping the crew is a convenience; a full or blocked storage must not fail the screen.
          await offline.store
            .putCrewCache({ ownerSubject: owner, subject, fetchedAtMs, crew })
            .catch(() => undefined);
        }

        const fetched: CrewResult = { crew, fetchedAtMs, fromDevice: false };
        return fetched;
      } catch (error) {
        const kept =
          owner !== null && error instanceof NetworkError
            ? await readKept(offline.store, owner)
            : null;

        if (kept === null) {
          throw error;
        }

        return kept;
      }
    },
    // The query runs without connection too, so that it can fall back to the kept crew.
    networkMode: "always",
    refetchInterval: crewRefreshMs,
    enabled: enabled && owner !== null,
  });

  return query;
}
